import { forceRefreshToken, getValidAccessTokenFromStorage } from "@weco/suite-client";
import { buildNotificationsWsUrl, NotificationSocket } from "./NotificationSocket";
import { cursorFromNext, notificationsApi, type NotificationsApi } from "./notificationsApi";
import type { AppNotification, ConnectionStatus, ServerFrame } from "./types";

/**
 * One per tab: the REST history merged with the live socket.
 *
 * The bell (desktop topbar and mobile sheet), the shell's toast/OS-banner
 * bridge and the incoming-call banner all read the same store, so there is
 * exactly one socket per tab however many of them are mounted. It runs while
 * at least one of them holds it (`start()` is reference counted) and resets
 * when the last lets go, so a sign-out never leaves one user's list on screen
 * for the next.
 */

export type LoadState = "idle" | "loading" | "ready" | "error";

export interface NotificationsSnapshot {
  /** Newest first. Retracted and expired notifications are never included. */
  items: AppNotification[];
  unread: number;
  loadState: LoadState;
  connection: ConnectionStatus;
  hasMore: boolean;
  loadingMore: boolean;
}

export interface SocketLike {
  start(): void;
  stop(): void;
  reconnectNow(): void;
}

export interface SocketHandlers {
  getSince: () => number;
  onFrame: (frame: ServerFrame) => void;
  onStatus: (status: ConnectionStatus) => void;
}

export interface NotificationsStoreDeps {
  api: NotificationsApi;
  createSocket: (handlers: SocketHandlers) => SocketLike;
  now?: () => number;
  /** Grace period before the last release really stops (absorbs StrictMode's remount). */
  stopDelayMs?: number;
  /** Coalesces count refreshes triggered by frames about notifications we have not loaded. */
  countRefreshDelayMs?: number;
}

type Listener = () => void;
type FrameListener = (frame: ServerFrame) => void;
type LiveListener = (n: AppNotification) => void;

const EMPTY: NotificationsSnapshot = {
  items: [],
  unread: 0,
  loadState: "idle",
  connection: "offline",
  hasMore: false,
  loadingMore: false,
};

export class NotificationsStore {
  private snapshot: NotificationsSnapshot = EMPTY;
  private byId = new Map<string, AppNotification>();
  private maxSeq = 0;
  private unread = 0;
  private loadState: LoadState = "idle";
  private connection: ConnectionStatus = "offline";
  private nextCursor: string | null = null;
  private loadingMore = false;
  /** Ids read or retracted during this session — a REST page fetched before the frame must not resurrect them. */
  private readIds = new Set<string>();
  private retractedIds = new Set<string>();
  /** True between a (re)connect and its `ready`: those frames are replay, not news. */
  private replaying = true;
  private readyCount = 0;
  private refreshGen = 0;

  private refs = 0;
  private running = false;
  private socket: SocketLike | null = null;
  private stopTimer: ReturnType<typeof setTimeout> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private countTimer: ReturnType<typeof setTimeout> | null = null;
  private detachWindow: (() => void) | null = null;

  private listeners = new Set<Listener>();
  private frameListeners = new Set<FrameListener>();
  private liveListeners = new Set<LiveListener>();

  constructor(private deps: NotificationsStoreDeps) {}

  // ── subscription ─────────────────────────────────────────────────────

  getSnapshot = (): NotificationsSnapshot => this.snapshot;

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Every server frame, after the store has applied it (incoming calls use this). */
  onFrame(listener: FrameListener): () => void {
    this.frameListeners.add(listener);
    return () => this.frameListeners.delete(listener);
  }

  /** Notifications that arrived live — not REST history, not reconnect replay. */
  onLive(listener: LiveListener): () => void {
    this.liveListeners.add(listener);
    return () => this.liveListeners.delete(listener);
  }

  // ── lifecycle ────────────────────────────────────────────────────────

  start(): () => void {
    this.refs += 1;
    if (this.stopTimer !== null) {
      clearTimeout(this.stopTimer);
      this.stopTimer = null;
    }
    if (!this.running) {
      this.running = true;
      this.socket = this.deps.createSocket({
        getSince: () => this.maxSeq,
        onFrame: (f) => this.handleFrame(f),
        onStatus: (s) => this.handleStatus(s),
      });
      this.attachWindow();
      this.socket.start();
      void this.refresh();
    }
    let released = false;
    return () => {
      if (released) return;
      released = true;
      this.refs = Math.max(0, this.refs - 1);
      if (this.refs === 0 && this.stopTimer === null) {
        this.stopTimer = setTimeout(() => {
          this.stopTimer = null;
          if (this.refs === 0) this.stopNow();
        }, this.deps.stopDelayMs ?? 1_000);
      }
    };
  }

  private stopNow(): void {
    this.running = false;
    this.socket?.stop();
    this.socket = null;
    this.detachWindow?.();
    this.detachWindow = null;
    for (const t of [this.expiryTimer, this.countTimer]) if (t !== null) clearTimeout(t);
    this.expiryTimer = null;
    this.countTimer = null;
    this.byId.clear();
    this.readIds.clear();
    this.retractedIds.clear();
    this.maxSeq = 0;
    this.unread = 0;
    this.loadState = "idle";
    this.connection = "offline";
    this.nextCursor = null;
    this.loadingMore = false;
    this.replaying = true;
    this.readyCount = 0;
    this.refreshGen += 1;
    this.emit();
  }

  private attachWindow(): void {
    if (typeof window === "undefined") return;
    const wake = () => this.socket?.reconnectNow();
    const onVisible = () => {
      if (document.visibilityState === "visible") wake();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", onVisible);
    this.detachWindow = () => {
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }

  // ── REST ─────────────────────────────────────────────────────────────

  /** (Re)load the first page and the unread count. Also the error state's retry. */
  async refresh(): Promise<void> {
    if (!this.running) return;
    const gen = ++this.refreshGen;
    if (this.byId.size === 0) {
      this.loadState = "loading";
      this.emit();
    }
    try {
      const [page, count] = await Promise.all([
        this.deps.api.list(),
        this.deps.api.unreadCount(),
      ]);
      if (gen !== this.refreshGen || !this.running) return;
      const fresh = page.results.map((n) => this.applyLocalState(n)).filter((n) => !this.retractedIds.has(n.id));
      const pageMax = fresh.reduce((m, n) => Math.max(m, n.seq), 0);
      // Anything the socket delivered while this request was in flight is
      // newer than the page and is kept; older pages loaded earlier are
      // dropped and paging restarts from the fresh cursor.
      const keep = [...this.byId.values()].filter((n) => n.seq > pageMax);
      this.byId.clear();
      for (const n of [...fresh, ...keep]) this.put(n);
      this.nextCursor = cursorFromNext(page.next);
      this.unread = count.total;
      this.loadState = "ready";
    } catch (err) {
      if (gen !== this.refreshGen || !this.running) return;
      console.warn("[notifications] load failed", err);
      this.loadState = this.byId.size > 0 ? "ready" : "error";
    }
    this.emit();
  }

  async loadMore(): Promise<void> {
    if (!this.running || !this.nextCursor || this.loadingMore) return;
    const gen = this.refreshGen;
    this.loadingMore = true;
    this.emit();
    try {
      const page = await this.deps.api.list({ cursor: this.nextCursor });
      if (gen !== this.refreshGen || !this.running) return;
      for (const n of page.results) {
        if (!this.byId.has(n.id) && !this.retractedIds.has(n.id)) this.put(this.applyLocalState(n));
      }
      this.nextCursor = cursorFromNext(page.next);
    } catch (err) {
      console.warn("[notifications] load more failed", err);
    } finally {
      if (gen === this.refreshGen) {
        this.loadingMore = false;
        this.emit();
      }
    }
  }

  markRead(id: string): void {
    const n = this.byId.get(id);
    if (n && !n.read_at) announceRead([n]);
    this.readIds.add(id);
    if (n && !n.read_at) {
      this.byId.set(id, { ...n, read_at: new Date(this.now()).toISOString() });
      this.unread = Math.max(0, this.unread - 1);
      this.emit();
    }
    this.deps.api.markRead(id).catch((err) => {
      console.warn("[notifications] mark read failed", err);
      this.scheduleCountRefresh();
    });
  }

  markAllRead(): void {
    announceRead([...this.byId.values()].filter((n) => !n.read_at));
    const at = new Date(this.now()).toISOString();
    for (const [id, n] of this.byId) {
      this.readIds.add(id);
      if (!n.read_at) this.byId.set(id, { ...n, read_at: at });
    }
    this.unread = 0;
    this.emit();
    this.deps.api.markAllRead().catch((err) => {
      console.warn("[notifications] mark all read failed", err);
      void this.refresh();
    });
  }

  // ── socket ───────────────────────────────────────────────────────────

  private handleStatus(status: ConnectionStatus): void {
    if (status !== "connected") this.replaying = true;
    this.connection = status;
    this.emit();
  }

  private handleFrame(frame: ServerFrame): void {
    switch (frame.type) {
      case "notification": {
        const { type: _type, ...rest } = frame;
        const n = this.applyLocalState(rest as AppNotification);
        if (this.retractedIds.has(n.id)) break;
        const known = this.byId.has(n.id);
        const prev = this.byId.get(n.id);
        this.put(n);
        if (!known && !n.read_at && !this.isExpired(n)) this.unread += 1;
        else if (prev && !prev.read_at && n.read_at) this.unread = Math.max(0, this.unread - 1);
        this.emit();
        if (!known && !this.replaying && !n.read_at && !this.isExpired(n)) {
          for (const l of this.liveListeners) safe(() => l(n));
        }
        break;
      }
      case "retracted": {
        let unknown = false;
        for (const id of frame.ids ?? []) {
          this.retractedIds.add(id);
          const n = this.byId.get(id);
          if (!n) {
            unknown = true;
            continue;
          }
          this.byId.delete(id);
          if (!n.read_at) this.unread = Math.max(0, this.unread - 1);
        }
        if (unknown) this.scheduleCountRefresh();
        this.emit();
        break;
      }
      case "read": {
        const at = new Date(this.now()).toISOString();
        if (frame.ids === "all") {
          const scoped = Boolean(frame.category || frame.group_key);
          for (const [id, n] of this.byId) {
            if (frame.category && n.category !== frame.category) continue;
            if (frame.group_key && n.group_key !== frame.group_key) continue;
            this.readIds.add(id);
            if (!n.read_at) this.byId.set(id, { ...n, read_at: at });
          }
          if (scoped) this.scheduleCountRefresh();
          else this.unread = 0;
        } else {
          let unknown = false;
          for (const id of frame.ids ?? []) {
            this.readIds.add(id);
            const n = this.byId.get(id);
            if (!n) {
              unknown = true;
              continue;
            }
            if (!n.read_at) {
              this.byId.set(id, { ...n, read_at: at });
              this.unread = Math.max(0, this.unread - 1);
            }
          }
          if (unknown) this.scheduleCountRefresh();
        }
        this.emit();
        break;
      }
      case "unread":
        this.unread = Math.max(0, Number(frame.total) || 0);
        this.emit();
        break;
      case "ready": {
        this.replaying = false;
        this.readyCount += 1;
        this.unread = Math.max(0, Number(frame.unread) || 0);
        this.emit();
        // A reconnect replays only what is still unread, so reads and
        // retractions made elsewhere while we were away need a REST resync.
        if (this.readyCount > 1 || this.loadState === "error") void this.refresh();
        break;
      }
      default:
        break;
    }
    for (const l of this.frameListeners) safe(() => l(frame));
  }

  // ── internals ────────────────────────────────────────────────────────

  private now(): number {
    return (this.deps.now ?? Date.now)();
  }

  private isExpired(n: AppNotification): boolean {
    if (!n.expires_at) return false;
    const t = Date.parse(n.expires_at);
    return Number.isFinite(t) && t <= this.now();
  }

  private applyLocalState(n: AppNotification): AppNotification {
    const normal: AppNotification = { ...n, data: n.data ?? {}, body: n.body ?? "" };
    if (!normal.read_at && this.readIds.has(n.id)) {
      normal.read_at = new Date(this.now()).toISOString();
    }
    return normal;
  }

  private put(n: AppNotification): void {
    this.byId.set(n.id, n);
    if (n.seq > this.maxSeq) this.maxSeq = n.seq;
  }

  private scheduleCountRefresh(): void {
    if (!this.running || this.countTimer !== null) return;
    this.countTimer = setTimeout(() => {
      this.countTimer = null;
      if (!this.running) return;
      const gen = this.refreshGen;
      this.deps.api
        .unreadCount()
        .then((c) => {
          if (gen !== this.refreshGen || !this.running) return;
          this.unread = c.total;
          this.emit();
        })
        .catch(() => {
          /* the next ready/refresh corrects it */
        });
    }, this.deps.countRefreshDelayMs ?? 300);
  }

  /** Drops expired notifications and arms a timer for the next one to expire. */
  private pruneExpired(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
    const now = this.now();
    let soonest = Infinity;
    for (const [id, n] of this.byId) {
      if (!n.expires_at) continue;
      const t = Date.parse(n.expires_at);
      if (!Number.isFinite(t)) continue;
      if (t <= now) {
        this.byId.delete(id);
        if (!n.read_at) this.unread = Math.max(0, this.unread - 1);
      } else if (t < soonest) {
        soonest = t;
      }
    }
    if (soonest !== Infinity && this.running) {
      this.expiryTimer = setTimeout(() => {
        this.expiryTimer = null;
        this.emit();
      }, Math.max(0, soonest - now) + 10);
    }
  }

  private emit(): void {
    this.pruneExpired();
    this.snapshot = {
      items: [...this.byId.values()].sort((a, b) => b.seq - a.seq),
      unread: this.unread,
      loadState: this.loadState,
      connection: this.connection,
      hasMore: this.nextCursor !== null,
      loadingMore: this.loadingMore,
    };
    for (const l of this.listeners) safe(l);
  }
}

/** Read in the bell: tells the module that owns these items (e.g. the Tasks
 *  Inbox, US 10.1) so it can mark its own copy read too. */
export const NOTIFICATIONS_READ_EVENT = "weco:notifications-read";
function announceRead(items: AppNotification[]): void {
  if (!items.length || typeof window === "undefined") return;
  try {
    window.dispatchEvent(new CustomEvent(NOTIFICATIONS_READ_EVENT, { detail: items }));
  } catch {
    /* an old browser without CustomEvent: the other copy stays unread */
  }
}

function safe(fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error("[notifications] listener", err);
  }
}

let _store: NotificationsStore | null = null;

/** The tab's store, built on first use (never at import time). */
export function getNotificationsStore(): NotificationsStore {
  return (_store ??= new NotificationsStore({
    api: notificationsApi,
    createSocket: (handlers) =>
      new NotificationSocket({
        url: () => buildNotificationsWsUrl(),
        getToken: () => getValidAccessTokenFromStorage(),
        refreshToken: () => forceRefreshToken(),
        ...handlers,
      }),
  }));
}
